/**
 * The confirm-then-build card.
 *
 * This is the only surface in the assistant that can create anything, and the
 * shape of it is the safety argument: everything on it was computed from the
 * archive in the browser, it is all editable, and **nothing has happened yet**
 * until the user presses Create.
 *
 * It is rendered for `message.kind === 'build'` BEFORE the markdown branch in
 * `AssistantMessage`. The message still carries a real markdown `content`
 * mirror — the sidebar search, the thread title, the Markdown export and the
 * history sent to the model all read `content` and nothing else.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import BuildStepList from './BuildStepList';
import { labelingRouteFor, modelTypeOf, toLabelingState } from '../../../utils/labelingTarget';

const MODE_LABEL_KEYS = {
  folders: 'assistant.build.mode.folders',
  cluster: 'assistant.build.mode.cluster',
  unlabeled: 'assistant.build.mode.unlabeled',
  annotations: 'assistant.build.mode.annotations',
};
const MODE_HINT_KEYS = {
  folders: 'assistant.build.modeHint.folders',
  cluster: 'assistant.build.modeHint.cluster',
  unlabeled: 'assistant.build.modeHint.unlabeled',
  annotations: 'assistant.build.modeHint.annotations',
};
const MODE_ICONS = {
  folders: 'fa-folder-open',
  cluster: 'fa-object-group',
  unlabeled: 'fa-images',
  annotations: 'fa-vector-square',
};
/** Notes the runner can emit. A whitelist, so an unknown code renders nothing. */
const NOTE_KEYS = {
  projectReused: 'assistant.build.note.projectReused',
  projectRenamed: 'assistant.build.note.projectRenamed',
  modelRenamed: 'assistant.build.note.modelRenamed',
  clusterSampled: 'assistant.build.note.clusterSampled',
  capped: 'assistant.build.note.capped',
  duplicates: 'assistant.build.note.duplicates',
  splitFolders: 'assistant.build.note.splitFolders',
  boxesFound: 'assistant.build.note.boxesFound',
  inventedClasses: 'assistant.build.note.inventedClasses',
  segmentFiles: 'assistant.build.note.segmentFiles',
  missingSize: 'assistant.build.note.missingSize',
};
const RESULT_KEYS = {
  done: 'assistant.build.result.done',
  partial: 'assistant.build.result.partial',
  failed: 'assistant.build.result.failed',
  cancelled: 'assistant.build.result.cancelled',
};

const modeOf = (plan) =>
  Object.prototype.hasOwnProperty.call(MODE_LABEL_KEYS, plan?.grouping?.mode)
    ? plan.grouping.mode
    : 'unlabeled';

const BuildCard = ({ message, run, isHeld, onRun, onCancel, onRenameClasses }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [names, setNames] = useState(null);

  const plan = message.plan;
  const state = message.run || null;
  const status = state?.status || 'idle';
  const mode = modeOf(plan);

  const classes = plan?.classes || [];
  const draftNames = names || classes.map((entry) => entry.name);

  const held = isHeld ? isHeld(message.attachmentId) : false;
  const isRunning = status === 'running';
  const isFinished = ['done', 'partial'].includes(status);
  const canRun = !isRunning && !isFinished && held;

  const notes = useMemo(() => {
    const list = [...((state && state.notes) || [])];
    if (plan?.limits?.dropped > 0) {
      list.unshift({
        codeKey: 'capped',
        params: { dropped: plan.limits.dropped, limit: plan.limits.willImport },
      });
    }
    return list.filter((note) => NOTE_KEYS[note.codeKey]);
  }, [plan, state]);

  if (!plan) return null;

  const saveNames = () => {
    onRenameClasses?.(message, draftNames);
    setEditing(false);
  };

  return (
    <article className="lf-card" aria-label={t('assistant.build.title')}>
      <header className="lf-card__head">
        <span className="lf-card__badge" aria-hidden="true">
          <i className="fa fa-cubes" />
        </span>
        <span className="lf-card__heading">
          <strong>{t('assistant.build.title')}</strong>
          <span className="lf-card__sub">
            {isFinished || status === 'failed'
              ? message.attachmentName
              : t('assistant.build.subtitle')}
          </span>
        </span>
        <span className="lf-card__mode">
          <i className={`fa ${MODE_ICONS[mode]}`} aria-hidden="true" /> {t(MODE_LABEL_KEYS[mode])}
        </span>
      </header>

      <dl className="lf-card__facts">
        <div>
          <dt>{t('assistant.build.project')}</dt>
          <dd>{state?.projectName || plan.target.projectName}</dd>
        </div>
        <div>
          <dt>{t('assistant.build.model')}</dt>
          <dd>{state?.modelName || plan.target.modelName}</dd>
        </div>
        <div>
          <dt>{t('assistant.build.imagesLabel')}</dt>
          <dd>{plan.limits.willImport}</dd>
        </div>
        <div>
          <dt>{t('assistant.build.classesLabel')}</dt>
          <dd>{classes.length || t('assistant.build.mode.unlabeled')}</dd>
        </div>
      </dl>

      <p className="lf-card__hint">{t(MODE_HINT_KEYS[mode])}</p>

      {!!classes.length && (
        <div className="lf-card__classes">
          {editing ? (
            <>
              {draftNames.map((name, index) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="lf-card__rename"
                  value={name}
                  placeholder={t('assistant.build.namePlaceholder')}
                  onChange={(event) => {
                    const next = [...draftNames];
                    next[index] = event.target.value;
                    setNames(next);
                  }}
                />
              ))}
              <button type="button" className="lf-card__mini" onClick={saveNames}>
                <i className="fa fa-check" aria-hidden="true" /> {t('common.save')}
              </button>
            </>
          ) : (
            <>
              {classes.map((entry) => (
                <span key={entry.id} className="lf-card__chip">
                  {entry.name}
                  {entry.count != null && <em>{entry.count}</em>}
                </span>
              ))}
              {canRun && (
                <button
                  type="button"
                  className="lf-card__mini"
                  onClick={() => {
                    setNames(classes.map((entry) => entry.name));
                    setEditing(true);
                  }}
                >
                  <i className="fa fa-pencil-alt" aria-hidden="true" /> {t('assistant.build.editNames')}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {!!notes.length && (
        <ul className="lf-card__notes">
          {notes.map((note, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${note.codeKey}-${index}`}>
              <i className="fa fa-info-circle" aria-hidden="true" /> {t(NOTE_KEYS[note.codeKey], note.params)}
            </li>
          ))}
        </ul>
      )}

      {status !== 'idle' && (
        <BuildStepList steps={run?.steps} percent={run?.percent} current={run?.current} />
      )}

      {status === 'failed' && state?.error && (
        <p className="lf-card__error" role="alert">
          <i className="fa fa-exclamation-triangle" aria-hidden="true" />{' '}
          {t(RESULT_KEYS.failed, { reason: state.error })}
        </p>
      )}

      {isFinished && (
        <p className={`lf-card__result${status === 'partial' ? ' lf-card__result--warn' : ''}`}>
          <i className={`fa ${status === 'done' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} aria-hidden="true" />{' '}
          {t(RESULT_KEYS[status], {
            imported: state.imported,
            expected: state.expected,
            model: state.modelName,
          })}
        </p>
      )}

      {status === 'cancelled' && <p className="lf-card__result">{t(RESULT_KEYS.cancelled)}</p>}

      {!held && !isFinished && status !== 'failed' && (
        <p className="lf-card__error" role="alert">
          <i className="fa fa-exclamation-triangle" aria-hidden="true" /> {t('assistant.attach.expired')}
        </p>
      )}

      <footer className="lf-card__foot">
        {canRun && (
          <button type="button" className="lf-card__run" onClick={() => onRun?.(message)}>
            <i className="fa fa-play" aria-hidden="true" /> {t('assistant.build.run')}
          </button>
        )}

        {isRunning && (
          <button type="button" className="lf-card__stop" onClick={() => onCancel?.(message.id)}>
            <i className="fa fa-stop" aria-hidden="true" /> {t('assistant.build.cancel')}
          </button>
        )}

        {(status === 'failed' || status === 'cancelled') && held && (
          <button type="button" className="lf-card__run" onClick={() => onRun?.(message)}>
            <i className="fa fa-redo" aria-hidden="true" /> {t('assistant.build.retry')}
          </button>
        )}

        {isFinished && (
          <>
            {state.modelDoc && labelingRouteFor(modelTypeOf(state.modelDoc)) && (
              <button
                type="button"
                className="lf-card__run"
                onClick={() =>
                  // A card can carry router state; a markdown link cannot, which
                  // is exactly why the prompt forbids linking these routes.
                  //
                  // The ROUTE follows the model's own type. Hardcoding
                  // /ClassificationLabel sent a detection import to the
                  // classification studio, which reads a different Mongo
                  // collection, finds nothing, and shows an empty screen that
                  // reads as a failed import.
                  navigate(labelingRouteFor(modelTypeOf(state.modelDoc)), {
                    state: toLabelingState(state.modelDoc, state.versionId),
                  })
                }
              >
                <i className="fa fa-tags" aria-hidden="true" /> {t('assistant.build.result.openLabeling')}
              </button>
            )}
            <button type="button" className="lf-card__link" onClick={() => navigate('/ModelManagement')}>
              {t('assistant.build.result.openModel')} <i className="fa fa-arrow-right" aria-hidden="true" />
            </button>
          </>
        )}
      </footer>
    </article>
  );
};

export default BuildCard;
